/**
 * The app's keyboard shortcuts (decision 16: keybindings; part 11 log, table K). Each command has a key the writer can
 * change in settings. A key is written as CodeMirror writes one (`Mod-Shift-f`: modifiers, then the key's name, `Mod`
 * being Cmd on a Mac and Ctrl elsewhere). A key the chapter editor binds is never taken from it: the list comes from
 * the editor's own keymaps (MISTAKES.md, "Key table listed the keys reported, not the keys bound").
 */
import { liveInput } from '@cockatrice/editor';
import { EditorState } from '@codemirror/state';
import type { Extension } from '@codemirror/state';
import { keymap } from '@codemirror/view';

/** What the app's shortcuts do. */
export const COMMANDS = ['focusMode', 'chapterList', 'previousChapter', 'nextChapter', 'newChapter', 'settings', 'help', 'find', 'note'] as const;

/** One of {@link COMMANDS}. */
export type Command = (typeof COMMANDS)[number];

/** Which modifier `Mod` is: Cmd on a Mac, Ctrl elsewhere. */
export type Platform = 'mac' | 'other';

/** Each command's key until the writer changes it (K1); none is one the editor binds or a browser keeps. */
export const DEFAULT_KEYS: Readonly<Record<Command, string>> = Object.freeze({
  focusMode: 'Mod-Shift-f',
  chapterList: 'Mod-Shift-e',
  previousChapter: 'Alt-PageUp',
  nextChapter: 'Alt-PageDown',
  newChapter: 'Mod-Shift-Enter',
  settings: 'Mod-,',
  // Part 11b, K10: the help, on the key most apps give it.
  help: 'F1',
  // Part 19a, F1: find and replace, on the key every app gives it; the browser's own find is kept from the page then.
  find: 'Mod-f',
  // Part 19e, Y′: a note on the passage chosen. Google Docs' Ctrl+Alt+M types on keyboards whose AltGr is Ctrl+Alt (K5).
  note: 'Mod-Shift-m',
});

/** Keys named by more than one character, beside letters, digits and F1–F24 (K2). */
const NAMED = new Set(['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Backspace', 'Delete', 'Insert', 'Space']);

/** The punctuation keys, by their `KeyboardEvent.code`, for a name Shift doesn't change (K7). */
const PUNCTUATION: Readonly<Record<string, string>> = Object.freeze({
  Comma: ',',
  Period: '.',
  Slash: '/',
  Semicolon: ';',
  Quote: "'",
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Minus: '-',
  Equal: '=',
  Backquote: '`',
});

const PUNCTUATION_NAMES: ReadonlySet<string> = new Set(Object.values(PUNCTUATION));

/** The keys an event names by the character they type: letters, digits and punctuation (K7). */
const TYPED_NAMES: ReadonlySet<string> = new Set([...Array.from('abcdefghijklmnopqrstuvwxyz0123456789'), ...PUNCTUATION_NAMES]);

/** Whether `name` is a key that types a character: a letter, a digit, punctuation or the space bar (K5). */
function isPrintable(name: string): boolean {
  return TYPED_NAMES.has(name) || name === 'Space';
}

/** Whether `name` is a key a shortcut may use (K2): Tab and Escape never, since keyboard use and dialogs need them. */
function isKeyName(name: string): boolean {
  return isPrintable(name) || /^F(?:[1-9]|1[0-9]|2[0-4])$/u.test(name) || NAMED.has(name);
}

/** A key with its modifiers resolved for one platform (K3). */
interface Chord {
  readonly alt: boolean;
  readonly ctrl: boolean;
  readonly meta: boolean;
  readonly shift: boolean;
  readonly name: string;
}

/** The canonical text of a chord, in CodeMirror's modifier order. */
function write(chord: Chord): string {
  let out = chord.name;
  if (chord.alt) out = `Alt-${out}`;
  if (chord.ctrl) out = `Ctrl-${out}`;
  if (chord.meta) out = `Meta-${out}`;
  if (chord.shift) out = `Shift-${out}`;
  return out;
}

/** Splits a key into its modifiers and its name (the name can be `-` itself), or null when it has no name. */
function parts(key: string): { readonly modifiers: readonly string[]; readonly name: string } | null {
  const match = /^((?:[^-]+-)*)([^-]+|-)$/u.exec(key);
  if (match === null) return null;
  const head = String(match[1]);
  return { modifiers: head === '' ? [] : head.slice(0, -1).split('-'), name: String(match[2]) };
}

/** Reads a key as CodeMirror writes it (K3), with `Mod` resolved for `platform`; null when it isn't a key. */
function readChord(key: string, platform: Platform): Chord | null {
  const split = parts(key);
  if (split === null) return null;
  // A letter's name is lower case; lowering any other one-character name changes nothing a key can be named.
  const chord = { alt: false, ctrl: false, meta: false, shift: false, name: split.name.length === 1 ? split.name.toLowerCase() : split.name };
  for (const modifier of split.modifiers) {
    const lower = modifier.toLowerCase();
    const flag = lower === 'mod' ? (platform === 'mac' ? 'meta' : 'ctrl') : lower === 'cmd' ? 'meta' : lower;
    if (flag !== 'alt' && flag !== 'ctrl' && flag !== 'meta' && flag !== 'shift') return null;
    if (chord[flag]) return null;
    chord[flag] = true;
  }
  return isKeyName(chord.name) ? chord : null;
}

/**
 * Reads a key as CodeMirror writes it (K3): modifiers (`Mod`, `Ctrl`, `Alt`, `Shift`, `Meta`, `Cmd`, in any order and
 * case, each once) joined to the key's name by `-`, with `Mod` resolved for `platform`. A letter's name is lower case.
 * Returns the canonical text, or null when it isn't a key.
 */
export function resolveKey(key: string, platform: Platform): string | null {
  const chord = readChord(key, platform);
  return chord === null ? null : write(chord);
}

/** The parts of a keyboard event a chord is read from. */
export interface KeyEventLike {
  readonly key: string;
  readonly code: string;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
}

/**
 * The canonical text of the key an event pressed (K7), or null for a key no shortcut uses (a modifier alone, Tab,
 * Escape). Letters, digits and punctuation are read from the key's position (`code`), so Alt on a Mac, which types
 * another character, and Shift still name the key.
 */
export function eventKey(event: KeyEventLike): string | null {
  const letter = /^Key([A-Z])$/u.exec(event.code);
  const digit = /^Digit([0-9])$/u.exec(event.code);
  const typed = event.key.length === 1 ? event.key.toLowerCase() : '';
  let name: string;
  if (TYPED_NAMES.has(typed)) name = typed;
  else if (letter !== null) name = String(letter[1]).toLowerCase();
  else if (digit !== null) name = String(digit[1]);
  else name = PUNCTUATION[event.code] ?? (event.key === ' ' ? 'Space' : event.key);
  if (!isKeyName(name)) return null;
  return write({ alt: event.altKey, ctrl: event.ctrlKey, meta: event.metaKey, shift: event.shiftKey, name });
}

/**
 * Every key the chapter editor binds on `platform` (K4), read from the keymaps of a real editor state with the
 * editor's input extensions (or `extensions`), with each binding's Shift variant. A binding this module can't read
 * (Tab, Escape) is a key no shortcut can take anyway.
 */
export function editorKeys(platform: Platform, extensions: Extension = liveInput()): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const bindings of EditorState.create({ extensions }).facet(keymap)) {
    for (const binding of bindings) {
      const names = platform === 'mac' ? [binding.mac ?? binding.key] : [binding.win ?? binding.key, binding.linux ?? binding.key];
      for (const name of names) {
        if (name === undefined) continue;
        const variants = binding.shift === undefined ? [name] : [name, `Shift-${name}`];
        for (const variant of variants) {
          const resolved = resolveKey(variant, platform);
          if (resolved !== null) keys.add(resolved);
        }
      }
    }
  }
  return keys;
}

/** Why a key can't be bound (K5): not a key, no modifier that keeps it from typing, the editor's, or another command's. */
export type BindingProblem =
  | { readonly kind: 'invalid' }
  | { readonly kind: 'typing' }
  | { readonly kind: 'editor' }
  | { readonly kind: 'taken'; readonly command: Command };

/** Every command's key; null for a command the writer left without one. */
export type KeyBindings = Readonly<Record<Command, string | null>>;

/**
 * Whether `key` can be `command`'s key (K5) given the other commands' keys: a key with Ctrl, Cmd or Alt, or a function
 * key alone, that the editor doesn't bind and no other command has. Returns the problem, or null.
 */
export function bindingProblem(bindings: KeyBindings, command: Command, key: string, platform: Platform, reserved: ReadonlySet<string>): BindingProblem | null {
  const chord = readChord(key, platform);
  if (chord === null) return { kind: 'invalid' };
  if (types(chord, platform)) return { kind: 'typing' };
  const resolved = write(chord);
  if (reserved.has(resolved)) return { kind: 'editor' };
  const other = COMMANDS.find((each) => {
    const key = bindings[each];
    return each !== command && key !== null && resolveKey(key, platform) === resolved;
  });
  return other === undefined ? null : { kind: 'taken', command: other };
}

/**
 * Whether a chord types text rather than leaving it to a shortcut (K5): no Ctrl, Cmd or Alt on a key that isn't a
 * function key; Option with a character key on a Mac, which types another character (`@` is Option-2 on a Norwegian
 * Mac); Ctrl+Alt with a character key elsewhere, which is AltGr on Windows (`@` is AltGr-2 there).
 */
function types(chord: Chord, platform: Platform): boolean {
  // Of the key names, only the function keys start with a capital F (letters are lower case).
  if (!chord.alt && !chord.ctrl && !chord.meta) return !chord.name.startsWith('F');
  if (!isPrintable(chord.name) || chord.meta) return false;
  return platform === 'mac' ? !chord.ctrl : chord.ctrl && chord.alt;
}

/**
 * The command `event` runs under `bindings` (K6), or null. An event the editor or a control already handled
 * (`defaultPrevented`) runs none, so a shortcut never takes a key from them.
 */
export function commandFor(bindings: KeyBindings, event: KeyEventLike & { readonly defaultPrevented: boolean }, platform: Platform): Command | null {
  if (event.defaultPrevented) return null;
  const pressed = eventKey(event);
  if (pressed === null) return null;
  return (
    COMMANDS.find((command) => {
      const key = bindings[command];
      return key !== null && resolveKey(key, platform) === pressed;
    }) ?? null
  );
}

/** A modifier key, in the order a key is shown (K8). */
export type Modifier = 'ctrl' | 'alt' | 'shift' | 'meta';

/** The named keys, for the words that show them (K8). */
export const NAMED_KEYS: readonly string[] = Object.freeze([...NAMED]);

/**
 * A key as the writer reads it (K8): its modifiers in the order platforms show them (Ctrl, Alt, Shift, Cmd), and its
 * name, a letter in upper case. Null when it isn't a key. The words or symbols for each are the interface's.
 */
export function keyParts(key: string, platform: Platform): { readonly modifiers: readonly Modifier[]; readonly name: string } | null {
  const chord = readChord(key, platform);
  if (chord === null) return null;
  return {
    modifiers: (['ctrl', 'alt', 'shift', 'meta'] as const).filter((modifier) => chord[modifier]),
    name: chord.name.length === 1 ? chord.name.toUpperCase() : chord.name,
  };
}
